import { AlertCircle, Upload } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import type { LoginStepProps } from "@/frontend/types/synthetic-hospital";

/**
 * Step 1 of the Synthetic Hospital login. Sample-patient dropdown, prefilled
 * password field, Login button, and a "Upload your own patient bundle"
 * link that opens the uploader on the parent view.
 */
export function LoginStep({
  samplePatients,
  loading,
  username,
  onUsernameChange,
  password,
  onPasswordChange,
  passwordError,
  onSubmit,
  onOpenUpload,
}: LoginStepProps) {
  return (
    <form onSubmit={onSubmit} className="rounded-xl border bg-card p-6 shadow-sm">
      <h2 className="text-lg font-semibold mb-4">Patient Login</h2>

      {passwordError && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{passwordError}</span>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="sh-patient" className="text-xs">Patient</Label>
          <select
            id="sh-patient"
            value={username}
            onChange={(event) => onUsernameChange(event.target.value)}
            disabled={loading}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading && <option>Loading sample patients…</option>}
            {!loading && samplePatients.length === 0 && <option value="">No sample patients available</option>}
            {samplePatients.map((samplePatient) => (
              <option key={samplePatient.username} value={samplePatient.username} disabled={samplePatient.claimed}>
                {samplePatient.displayName}{samplePatient.claimed ? " (already connected)" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sh-password" className="text-xs">Password</Label>
          <Input
            id="sh-password"
            type="password"
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
            autoComplete="off"
          />
          <p className="text-[11px] text-muted-foreground">
            Password is <code className="rounded bg-muted px-1 py-0.5 font-mono">demopass</code> for every test patient.
          </p>
        </div>
        <Button
          type="submit"
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={loading || !username}
        >
          Login
        </Button>
      </div>
      <div className="mt-4 pt-4 border-t text-center">
        <button
          type="button"
          onClick={onOpenUpload}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <Upload className="size-3" />
          Upload your own patient bundle
        </button>
      </div>
    </form>
  );
}
